import { JSX, useCallback, useEffect, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { updateDossier } from "../../../app/rootReducer";
import { HowToCloud } from "../../../common/components/howTo/HowToCloud";
import { BovagTermsTypeEnum } from "../../../common/constants/BovagTermsTypeEnum";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { PatchStepsEnum } from "../../../common/constants/PatchStepsEnum";
import { dossierTypeEnum } from "../../../common/constants/dossierTypeEnum";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import { setLoanType } from "../appointmentSlice";
import { scheduleScrollToStep } from "../../../common/helpers/scrollToStep";

type LoanVehicleCode = "bike" | "pickup" | "car" | "none";

type LoanVehicleOption = Readonly<{
    type: LoanVehicleCode;
    icon: string;
    text: string;
    howToCode?: string;
    variant?: "default" | "voucher";
}>;

type LoanVehicleProps = LoanVehicleOption & {
    isActive: boolean;
    onSelect: (type: LoanVehicleCode) => void;
};

const LOAN_VEHICLE_OPTIONS: readonly LoanVehicleOption[] = [
    {
        type: "bike",
        icon: "biycle",
        text: "Ik wil een leenfiets",
        howToCode: "borrowBicycle",
    },
    {
        type: "pickup",
        icon: "bus",
        text: "Ik wil haal- en brengservice",
        howToCode: "pickup",
    },
    {
        type: "car",
        icon: "car-front",
        text: "Ik wil een leenauto",
        howToCode: "borrowCar",
    },
    {
        type: "none",
        icon: "",
        text: "Ik wil geen leenvoertuig",
    },
];

const LoanVehicle = ({
    text,
    type,
    isActive,
    onSelect,
    variant,
}: LoanVehicleProps): JSX.Element => {
    let buttonClassName = [
        "c-form__buttons__single",
        "border-color-primary",
        "border-color-primary-hover",
        isActive ? "button-active" : null,
    ]
        .filter((v): v is string => typeof v === "string")
        .join(" ");

    if (variant === "voucher") {
        buttonClassName += " c-form__buttons__single--voucher";
    }

    return (
        <button
            type="button"
            className={buttonClassName}
            onClick={() => onSelect(type)}
        >
            <span className="font-face-primary">{text}</span>
        </button>
    );
};

type LoanVehicleWithVoucherNoteProps = {
    isActive: boolean;
    contractorName?: string;
    onSelect: () => void;
};

const LoanVehicleWithVoucherNote = ({
    isActive,
    contractorName,
    onSelect,
}: LoanVehicleWithVoucherNoteProps): JSX.Element => {
    const buttonClassName = [
        "c-form__buttons__single",
        "c-form__buttons__single--voucher-green",
        isActive ? "c-form__buttons__single--voucher-active" : null,
    ]
        .filter((v): v is string => typeof v === "string")
        .join(" ");

    return (
        <button type="button" className={buttonClassName} onClick={onSelect}>
            <div className="c-loan-vehicle-voucher">
                <div className="c-loan-vehicle-voucher__content">
                    <span className="font-face-primary">
                        Ik wil geen leenvoertuig
                    </span>
                    <p className="c-loan-vehicle-voucher__note">
                        Ik ontvang een {contractorName ?? ""} cadeaubon t.w.v.
                        €100,-
                    </p>
                </div>

                <div className="c-loan-vehicle-voucher__badge">
                    <div className={"c-loan-vehicle-voucher__badge-icon"}>
                        <img src={"/images/eco.svg"} alt={"Duurzaam"} />
                    </div>
                    <p className={"c-loan-vehicle-voucher__badge-text"}>
                        Duurzame keuze
                    </p>
                </div>
            </div>
        </button>
    );
};

const AppointmentLoanVehicle = (): JSX.Element | null => {
    const dispatch = useAppDispatch();

    const { dossier, dossierOptions, registrationLink } = useAppSelector(
        (state) => state.root,
    );
    const { activeHelpType } = useAppSelector((state) => state.helpType);
    const { agendaMoments, selectedTime, selectedDate, loanType } =
        useAppSelector((state) => state.appointment);
    const { lease, postMissingContractor, showContactorNotice, vehicleUsage } =
        useAppSelector((state) => state.vehicleInformation);

    const initialMount = useRef(true);
    const scrollRef = useRef<HTMLDivElement>(null);

    const shouldRender = useMemo(() => {
        const hasAppointmentSelection =
            !!dossier &&
            !!selectedDate &&
            !!agendaMoments.activeAgendaMoment &&
            !!selectedTime?.startTime &&
            !!selectedTime?.endTime &&
            (activeHelpType === dossierTypeEnum.BodyRepair ||
                activeHelpType === dossierTypeEnum.CaravanRepair);

        const noAppointmentsButLoanVehicles =
            !!dossier &&
            !dossierOptions.allowsAppointments &&
            dossierOptions.allowsLoanVehicles;

        return hasAppointmentSelection || noAppointmentsButLoanVehicles;
    }, [
        dossier,
        selectedDate,
        agendaMoments.activeAgendaMoment,
        selectedTime?.startTime,
        selectedTime?.endTime,
        activeHelpType,
        dossierOptions.allowsAppointments,
        dossierOptions.allowsLoanVehicles,
    ]);

    const contractorName = dossier?.contractor?.name;
    const hasSustainabilityVoucher =
        !!dossier?.contractor?.has_sustainability_voucher;

    const handleSelect = useCallback(
        (type: LoanVehicleCode) => {
            dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
            dispatch(setLoanType(type));
        },
        [dispatch],
    );

    useEffect(() => {
        if (!shouldRender) return;
        scheduleScrollToStep(() => scrollRef.current);
    }, [shouldRender]);

    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }

        if (!loanType) return;

        dispatch(
            updateDossier({
                data: { loan_vehicle_code: loanType },
                method: PatchStepsEnum.PatchLoanVehicle,
            }),
        ).then(() => {
            dispatch(setGeneralStep(GeneralStepsEnum.Form));
            setTimeout(() => {
                scheduleScrollToStep(() => scrollRef.current);
            }, 100);
        });
    }, [loanType, dispatch]);

    if (!dossierOptions.allowsLoanVehicles) return null;
    if (!shouldRender) return null;

    const leftOptions = LOAN_VEHICLE_OPTIONS.filter((o) => o.type !== "none");
    const hasNoneOption = LOAN_VEHICLE_OPTIONS.some((o) => o.type === "none");

    const showVoucherLayout = hasSustainabilityVoucher && hasNoneOption;

    return (
        <div
            className="s-appointment__group border-color-primary"
            ref={scrollRef}
        >
            <div className="s-appointment__group__header">
                <h1 className="s-appointment__group__title s-appointment__group__title--no-margin">
                    Heb je vervangend vervoer nodig?
                </h1>
                <HowToCloud
                    code="borrowVehicles"
                    contractorName={contractorName}
                    showVoucherLayout={showVoucherLayout}
                />
            </div>

            <p>
                Onderstaande opties bij beschikbaarheid. De vestiging neemt
                hierover contact op.
            </p>

            {showVoucherLayout && (
                <div className="c-loan-vehicle-info">
                    <div className={"c-loan-vehicle-info__icon"}>
                        <img src="/images/eco.svg" alt="Duurzame keuze" />
                    </div>
                    <p className={"c-loan-vehicle-info__text"}>
                        Kies je ervoor om geen vervangend vervoer te gebruiken?
                        Dan ontvang je bij het ophalen van je auto een cadeaubon
                        van €100.
                    </p>
                </div>
            )}

            <div className="c-form__group">
                {showVoucherLayout ? (
                    <div className="c-loan-vehicle-layout c-loan-vehicle-layout--voucher">
                        <div className="c-loan-vehicle-layout__left c-form__buttons c-form__buttons--start">
                            {leftOptions.map((option) => (
                                <LoanVehicle
                                    {...option}
                                    key={option.type}
                                    isActive={loanType === option.type}
                                    onSelect={handleSelect}
                                    variant={"voucher"}
                                />
                            ))}
                        </div>

                        <div className="c-loan-vehicle-layout__right">
                            <LoanVehicleWithVoucherNote
                                isActive={loanType === "none"}
                                contractorName={contractorName}
                                onSelect={() => handleSelect("none")}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="c-form__buttons c-form__buttons--start">
                        {LOAN_VEHICLE_OPTIONS.map((option) => (
                            <LoanVehicle
                                {...option}
                                key={option.type}
                                isActive={loanType === option.type}
                                onSelect={handleSelect}
                            />
                        ))}
                    </div>
                )}
            </div>

            {loanType === "car" &&
                (!!lease.activeContractorId || postMissingContractor) &&
                registrationLink.business_lease &&
                registrationLink.private_lease &&
                !showContactorNotice &&
                vehicleUsage === BovagTermsTypeEnum.PRIVATE && (
                    <div className="c-loan-vehicle-info c-loan-vehicle-info--blue">
                        <div className="c-loan-vehicle-info__icon">
                            <img
                                src="/images/info-icon-private-lease.svg"
                                alt="Private lease informatie"
                            />
                        </div>
                        <p className="c-loan-vehicle-info__text">
                            Let op: vervangend vervoer is bij private lease niet
                            altijd inbegrepen. Bekijk je leasecontract om te
                            zien wat voor jou geldt.
                        </p>
                    </div>
                )}
        </div>
    );
};

export default AppointmentLoanVehicle;
